import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Loader2, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import axios from 'axios';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const { t, language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const txt = staticTexts.events;
  const commonTxt = staticTexts.common;

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get event title
  const getEventTitle = (event) => {
    if (language === 'ar' && event.title_ar) return event.title_ar;
    if (language === 'fr' && event.title_fr) return event.title_fr;
    return event.title_en || event.title_fr || '';
  };

  // Helper to get event description
  const getEventDescription = (event) => {
    if (language === 'ar' && event.description_ar) return event.description_ar;
    if (language === 'fr' && event.description_fr) return event.description_fr;
    return event.description_en || event.description_fr || '';
  };

  // Check if event is in the past
  const isPastEvent = (event) => {
    if (!event.date) return false;
    try {
      const eventDate = new Date(event.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return eventDate < today;
    } catch {
      return false;
    }
  };

  // Calculate days until event
  const getDaysUntil = (event) => {
    if (!event.date) return null;
    try {
      const eventDate = new Date(event.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);
      const diffTime = eventDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  // Get urgency badge for upcoming events
  const getUrgencyBadge = (daysUntil) => {
    if (daysUntil === null || daysUntil < 0) return null;
    
    if (daysUntil === 0) {
      return {
        text: getText(language, "Today!", "Aujourd'hui !", "اليوم!"),
        className: 'bg-red-500 text-white animate-pulse'
      };
    }
    if (daysUntil === 1) {
      return {
        text: getText(language, "Tomorrow!", "Demain !", "غداً!"),
        className: 'bg-red-500 text-white'
      };
    }
    if (daysUntil <= 3) {
      return {
        text: getText(language, `In ${daysUntil} days`, `Dans ${daysUntil} jours`, `خلال ${daysUntil} أيام`),
        className: 'bg-orange-500 text-white'
      };
    }
    if (daysUntil <= 7) {
      return {
        text: getText(language, `In ${daysUntil} days`, `Dans ${daysUntil} jours`, `خلال ${daysUntil} أيام`),
        className: 'bg-yellow-500 text-white'
      };
    }
    return null;
  };

  // Separate upcoming and past events
  const upcomingEvents = events.filter(e => !isPastEvent(e));
  const pastEvents = events.filter(e => isPastEvent(e));

  // Event Card Component
  const EventCard = ({ event, isPast = false }) => {
    const daysUntil = getDaysUntil(event);
    const urgencyBadge = !isPast ? getUrgencyBadge(daysUntil) : null;
    
    return (
      <div 
        className={`bg-white rounded-2xl overflow-hidden shadow-sm transition-all ${
          isPast ? 'opacity-60 grayscale' : 'hover:shadow-lg'
        } ${urgencyBadge ? 'ring-2 ring-offset-2 ' + (daysUntil <= 1 ? 'ring-red-500' : daysUntil <= 3 ? 'ring-orange-500' : 'ring-yellow-500') : ''}`}
      >
        <div className="relative">
          {event.image_url && (
            <img
              src={event.image_url}
              alt={getEventTitle(event)}
              className="w-full h-48 object-cover"
            />
          )}
          {/* Urgency Badge */}
          {urgencyBadge && (
            <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-sm font-bold shadow-lg ${urgencyBadge.className}`}>
              {urgencyBadge.text}
            </div>
          )}
        </div>
        <div className="p-6">
          {isPast && (
            <span className="inline-block px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded mb-3">
              {getText(language, 'Past Event', 'Événement passé', 'حدث سابق')}
            </span>
          )}
          <h3 className="text-xl font-medium text-slate-800 mb-3">
            {getEventTitle(event)}
          </h3>
          <div className="flex flex-col gap-2 text-sm text-slate-500 mb-4">
            <span className="flex items-center gap-2">
              <Calendar size={14} />
              {event.date}
            </span>
            <span className="flex items-center gap-2">
              <MapPin size={14} />
              {event.location}
              {event.maps_url && (
                <a 
                  href={event.maps_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={12} />
                  {getText(language, 'View on Maps', 'Voir sur Maps', 'عرض على الخريطة')}
                </a>
              )}
            </span>
          </div>
          <p className="text-slate-600">
            {getEventDescription(event)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.upcomingEvents.en, txt.upcomingEvents.fr, txt.upcomingEvents.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
              <p className="text-slate-500 mt-4">
                {getText(language, commonTxt.loading.en, commonTxt.loading.fr, commonTxt.loading.ar)}
              </p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center max-w-xl mx-auto py-12">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-sm">
                <Calendar className="text-slate-300" size={36} strokeWidth={1.5} />
              </div>
              <p className="text-slate-500">
                {getText(language, txt.noEvents.en, txt.noEvents.fr, txt.noEvents.ar)}
              </p>
            </div>
          ) : (
            <>
              {/* Upcoming Events */}
              {upcomingEvents.length > 0 && (
                <div className="mb-16">
                  <h2 className="text-2xl font-light text-slate-800 mb-8">
                    {getText(language, 'Upcoming Events', 'Événements à venir', 'الأحداث القادمة')}
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {upcomingEvents.map((event) => (
                      <EventCard key={event.id} event={event} isPast={false} />
                    ))}
                  </div>
                </div>
              )}

              {/* Past Events */}
              {pastEvents.length > 0 && (
                <div>
                  <h2 className="text-2xl font-light text-slate-500 mb-8 flex items-center gap-3">
                    {getText(language, 'Past Events', 'Événements passés', 'الأحداث السابقة')}
                    <span className="text-sm font-normal text-slate-400">({pastEvents.length})</span>
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {pastEvents.map((event) => (
                      <EventCard key={event.id} event={event} isPast={true} />
                    ))}
                  </div>
                </div>
              )}

              {/* If only past events and no upcoming */}
              {upcomingEvents.length === 0 && pastEvents.length > 0 && (
                <div className="text-center mb-12 p-6 bg-blue-50 rounded-lg">
                  <p className="text-blue-800">
                    {getText(
                      language, 
                      'No upcoming events. Check back soon!', 
                      'Pas d\'événements à venir. Revenez bientôt !', 
                      'لا توجد أحداث قادمة. عد قريباً!'
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default EventsPage;
